import { useParams } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Icon } from "@/lib/icons";
import { useApp } from "@/hooks/use-app-context";
import type { ValidationGate } from "@/types/auth.types";

export function ValidationGateDialog({
  open,
  onOpenChange,
  gate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gate: ValidationGate;
}) {
  const { t } = useApp();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || "fr";

  const progressPercent = gate.validationsRequired > 0
    ? Math.min(100, Math.round((gate.validationsSinceBlock / gate.validationsRequired) * 100))
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon name="lock" className="h-5 w-5 text-brand" />
            {t.validationGateBlockedTitle}
          </DialogTitle>
          <DialogDescription>
            {t.validationGateBlockedDesc
              .replace("{threshold}", String(gate.blockThreshold))
              .replace("{required}", String(gate.validationsRequired))}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-foreground">
              {t.validationGateProgress
                .replace("{done}", String(gate.validationsSinceBlock))
                .replace("{total}", String(gate.validationsRequired))}
            </span>
            <span className="text-muted-foreground/70">{progressPercent}%</span>
          </div>
          <Progress value={progressPercent} />
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end sm:space-x-0">
          <a href={`/${currentLang}/validate-audio`} className="w-full sm:w-auto">
            <Button className="h-11 w-full rounded-full">
              <Icon name="checkCircle" className="mr-2 h-4 w-4" />
              {t.validationGateGoValidateAudio}
            </Button>
          </a>
          <a href={`/${currentLang}/validate-transcription`} className="w-full sm:w-auto">
            <Button variant="outline" className="h-11 w-full rounded-full">
              <Icon name="checkCircle" className="mr-2 h-4 w-4" />
              {t.validationGateGoValidateTranscription}
            </Button>
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
